import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import { API } from "./utils";

const LANG_KEY = "cowrieking_lang";
const CACHE_KEY = "cowrieking_translations_v1";

const I18nContext = createContext({
  lang: "en",
  setLang: () => {},
  t: (txt) => txt,
  languages: [],
});

export function I18nProvider({ children }) {
  const [lang, setLangState] = useState(() => localStorage.getItem(LANG_KEY) || "en");
  const [languages, setLanguages] = useState([
    { code: "en", name: "English" },
    { code: "fr", name: "French" },
    { code: "es", name: "Spanish" },
    { code: "tw", name: "Twi (Akan)" },
    { code: "ga", name: "Ga (Ghana)" },
    { code: "ee", name: "Ewe (Ghana)" },
  ]);
  const [cache, setCache] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(CACHE_KEY) || "{}");
    } catch {
      return {};
    }
  });
  const [, force] = useState(0);

  useEffect(() => {
    axios.get(`${API}/translate/languages`).then((r) => setLanguages(r.data)).catch(() => {});
  }, []);

  const setLang = useCallback((code) => {
    localStorage.setItem(LANG_KEY, code);
    setLangState(code);
  }, []);

  // Fire-and-forget translation; show original meanwhile.
  const t = useCallback(
    (text) => {
      if (!text || lang === "en") return text;
      const k = `${lang}::${text}`;
      if (cache[k]) return cache[k];
      // queue translation request (debounced via single-flight per key)
      if (!t.__pending) t.__pending = new Set();
      if (!t.__pending.has(k)) {
        t.__pending.add(k);
        axios
          .post(`${API}/translate`, { text, target_lang: lang })
          .then((r) => {
            const translated = r.data?.translated || text;
            setCache((prev) => {
              const next = { ...prev, [k]: translated };
              try { localStorage.setItem(CACHE_KEY, JSON.stringify(next)); } catch {}
              return next;
            });
            force((n) => n + 1);
          })
          .catch(() => {})
          .finally(() => t.__pending.delete(k));
      }
      return text;
    },
    [lang, cache]
  );

  const value = useMemo(() => ({ lang, setLang, t, languages }), [lang, setLang, t, languages]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export const useI18n = () => useContext(I18nContext);

/** <T>Some English text</T> — renders translated text inline */
export function T({ children }) {
  const { t } = useI18n();
  if (typeof children !== "string") return children;
  return <>{t(children)}</>;
}
